import MainLayout from "@/components/layout/admin/MainLayout.vue";
import Select2Multiple from "v-select2-multiple-component";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import axios from "axios";
import { required } from "vuelidate/lib/validators";

const maxSize5MB = (value) => !value || value.size <= 5 * 1024 * 1024;

export default {
  name: "GradeFeeImport",

  components: {
    MainLayout,
    Select2Multiple,
    Loading,
  },

  data() {
    return {
      isLoading: false,
      disableImportBtn: false,
      disableConfirmBtn: false,
      disableInputFields: false,
      disableAcademicYear: false,
      submitted: false,
      templateSubmitted: false,
      importStep: "form",
      periodArr: [],
      template_academic_year_id: null,
      academic_year_id: null,
      file_name: "",
      selectedFile: null,
      invalidFileType: false,
      previewRows: [],
      previewTotal: 0,
    };
  },

  validations: {
    academic_year_id: { required },
    selectedFile: { required, maxSize5MB },
  },

  computed: {
    disabledTemplateBtn() {
      return !this.template_academic_year_id || this.disableInputFields;
    },

    selectedAcademicYearName() {
      const selected = this.periodArr.find(
        (period) => String(period.id) === String(this.academic_year_id)
      );

      return selected?.text || this.previewRows[0]?.academic_year_name || "";
    },

    isPreviewStep() {
      return this.importStep === "preview";
    },
  },

  methods: {
    getAcademicYear() {
      this.isLoading = true;
      axios.get("period/periodList")
        .then((res) => {
          this.periodArr = res.data.data || [];
        })
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    chooseFile() {
      if (this.isPreviewStep) {
        return;
      }

      this.$refs.fileUploader.value = null;
      this.selectedFile = null;
      this.file_name = "";
      this.invalidFileType = false;
      this.$refs.fileUploader.click();
    },

    onFileChange(event) {
      const file = event.target.files[0];
      this.selectedFile = null;
      this.file_name = "";
      this.invalidFileType = false;

      if (!file) {
        return;
      }

      const extension = file.name.split(".").pop()?.toLowerCase();
      if (!["xls", "xlsx"].includes(extension)) {
        this.invalidFileType = true;
        return;
      }

      this.selectedFile = file;
      this.file_name = file.name;
    },

    getTemplateFileName() {
      const selected = this.periodArr.find(
        (period) => String(period.id) === String(this.template_academic_year_id)
      );
      const periodName = (selected?.text || "grade_fees").replace(/\s+/g, "_");
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");

      return `${periodName}_grade_fees_${year}_${month}_${day}.xlsx`;
    },

    downloadTemplate() {
      this.templateSubmitted = true;

      if (this.disabledTemplateBtn) {
        return;
      }

      this.isLoading = true;
      axios.get("gradeFee/excelTemplateDownload", {
        params: {
          academic_year_id: this.template_academic_year_id,
        },
        responseType: "blob",
      })
        .then((response) => {
          const blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const link = document.createElement("a");

          link.href = window.URL.createObjectURL(blob);
          link.download = this.getTemplateFileName();
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch((error) => {
          this.handleError(error, "Failed to download the grade fee template.");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    confirmImport() {
      this.submitted = true;
      this.$v.$touch();

      if (this.$v.$invalid || this.invalidFileType) {
        return;
      }

      this.validateImport();
    },

    validateImport() {
      this.isLoading = true;
      this.disableImportBtn = true;
      this.disableInputFields = true;
      this.disableAcademicYear = true;

      const formData = new FormData();
      formData.append("academic_year_id", this.academic_year_id);
      formData.append("file", this.selectedFile);

      axios.post("gradeFee/excelImportValidate", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
        .then((response) => {
          this.previewRows = response.data.preview || [];
          this.previewTotal = response.data.total || this.previewRows.length;
          this.importStep = "preview";
        })
        .catch((error) => {
          this.handleValidationError(error);
          this.disableInputFields = false;
          this.disableImportBtn = false;
          this.disableAcademicYear = false;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    confirmImportSubmit() {
      if (!this.previewRows.length) {
        this.$notification.error("No preview data to import.", {
          timer: 3,
          position: "bottomCenter",
        });
        return;
      }

      this.isLoading = true;
      this.disableConfirmBtn = true;

      axios.post("gradeFee/excelImportConfirm", {
        academic_year_id: this.academic_year_id,
        preview: this.previewRows,
      })
        .then((response) => {
          this.$notification.success(response.data.message || "Grade fees imported successfully.", {
            timer: 3,
            position: "bottomCenter",
          });
          this.showList();
        })
        .catch((error) => {
          if (error.response?.status === 422) {
            this.handleValidationError(error);
            this.importStep = "form";
            this.previewRows = [];
            this.previewTotal = 0;
            this.disableInputFields = false;
            this.disableImportBtn = false;
            this.disableAcademicYear = false;
          } else {
            this.handleError(error);
          }
        })
        .finally(() => {
          this.isLoading = false;
          this.disableConfirmBtn = false;
        });
    },

    handleValidationError(error) {
      if (error.response?.status === 422) {
        const data = error.response.data || {};
        const messages = data.errors?.length
          ? data.errors
          : [data.message || "Import validation failed."];

        alert(messages.join("\n"));
        return;
      }

      this.handleError(error);
    },

    resetForm() {
      this.submitted = false;
      this.templateSubmitted = false;
      this.importStep = "form";
      this.disableInputFields = false;
      this.disableAcademicYear = false;
      this.disableImportBtn = false;
      this.disableConfirmBtn = false;
      this.previewRows = [];
      this.previewTotal = 0;
      this.template_academic_year_id = null;
      this.academic_year_id = null;
      this.selectedFile = null;
      this.file_name = "";
      this.invalidFileType = false;
      this.$v.$reset();

      if (this.$refs.fileUploader) {
        this.$refs.fileUploader.value = null;
      }
    },

    showList() {
      this.$router.push({ name: "gradeFeeList" });
    },

    formatFee(value) {
      if (value === null || value === undefined || value === "") {
        return "-";
      }

      return Number(value).toLocaleString();
    },

    handleError(error, fallbackMessage = "Something went wrong.") {
      if (error.response?.status === 401) {
        this.$store.commit("logout");
        window.location.replace("login");
        return;
      }

      const message = error.response?.data?.message || fallbackMessage;
      console.error(error);
      this.$notification.error(message, {
        timer: 3,
        position: "bottomCenter",
      });
    },
  },

  mounted() {
    this.getAcademicYear();
  },
};
